import { NetworkError } from '@workspace/network';
import type { ApiMessage, ToolCallWithResult, V1OutputItem, ContentPart, ReasoningLevel, AttachmentOutcome } from '../types';
import { llmApiClient } from './network';
// From the dedicated module, NOT './capabilities': that one imports the
// catalog, which builds 40+ zod schemas at module scope and would put ~92KB
// gzipped on the critical path for a static string.
import { CAPABILITY_SUMMARY } from '@workspace/gen-ui/capability-summary';
import { toolCapabilitySummary } from '@workspace/mcp-client/tool-capability-summary';

export interface GenerationSettings {
  temperature?: number;
  maxTokens?: number;
  topP?: number;
  topK?: number;
  minP?: number;
  repeatPenalty?: number;
  presencePenalty?: number;
  frequencyPenalty?: number;
  repetitionPenalty?: number;
  seed?: number;
  stop?: string[];
}

// ============================================================================
// LM Studio v1 Chat API (/api/v1/chat) — Server-side MCP
// ============================================================================

// v1 API input item types
export type V1InputItem =
  // INPUT text is `text`, not `message`. Verified against a live worker
  // 2026-08-20: `message` is rejected with "Invalid discriminator value.
  // Expected 'text' | 'image'". `message` is what appears on OUTPUT, which is
  // the easy confusion — sending it made every attachment turn fail at the
  // worker with a 400.
  | { type: 'text'; content: string }
  | { type: 'image'; data_url: string; storage_key?: string }
  // Not part of LM Studio's v1 API — our own extension, consumed and rewritten
  // by the LB's fileUrlProcessor (to inline text or a retrieval handle) before
  // the request is forwarded. It never reaches LM Studio in this form.
  | { type: 'file'; url: string; filename?: string }
  // Role-tagged history. Not LM Studio's item form — the LB accepts it directly
  // (`buildMessagesFromV1Request` reads `{ role, content }` alongside items) and
  // it is how the whole conversation reaches the stateless orchestrator path.
  | { role: string; content: string };

export interface StreamV1ChatResult {
  content: string;
  toolCalls: ToolCallWithResult[];
  reasoning: string;
  responseId?: string;
}

export interface StreamV1ChatOptions {
  apiUrl: string;
  apiKey?: string;
  messages: ApiMessage[];
  model: string;
  modelCapabilities?: string[];
  previousResponseId?: string;
  /** MCP server IDs from mcp.json to enable (e.g. ["mcp/mc-server-hub"]) */
  mcpServers?: string[];
  /**
   * Signpost the owner-only `knowledge` server in the system prompt. True only
   * when this user's tool list (the LB's `/v1/tools`, which drops tools an
   * upstream restricted to other users) contains it; every other user would
   * pay for a line about a tool that refuses them (tdd-0103 S4).
   */
  includeKnowledgeSummary?: boolean;
  /** Signpost the owner-only `apple-bridge` server; same rule as `includeKnowledgeSummary` (tdd-0106). */
  includeAppleSummary?: boolean;
  settings?: GenerationSettings;
  reasoningLevel?: ReasoningLevel;
  /** Opt into the LB's centralized Brain recall via `x-brain-recall: on` (tdd-0042). */
  enableMemory?: boolean;
  /**
   * Which conversation this turn belongs to, so a result the client never
   * received can still be written back to it (tdd-0090).
   *
   * The LB has never known this — "session" there means sticky worker routing,
   * not a chat thread — which is why a turn whose socket died had nowhere to
   * put its output and simply lost it.
   */
  sessionId?: string;
  /**
   * Whether this conversation may be persisted server-side at all.
   *
   * Explicit rather than inferred from `sessionId` being present. A private
   * thread is guaranteed never to be written anywhere (tdd-0042), and a server
   * left to DEDUCE that will eventually deduce wrong — where the failure is a
   * privacy breach rather than a lost message. Sending `false` costs nothing;
   * omitting the signal and guessing does not.
   */
  persistable?: boolean;
  /** The LB recalled these memories (+ the query) and injected them into the prompt. */
  onRecalledMemories?: (facts: string[], query?: string) => void;
  /** What the LB did with each attachment — inlined, indexed, truncated or failed (tdd-0062). */
  onAttachmentsProcessed?: (attachments: AttachmentOutcome[]) => void;
  onChunk: (content: string) => void;
  onReasoningChunk?: (content: string) => void;
  onToolCallStart?: (toolName: string) => void;
  /** Arguments for an in-flight call, so the pending card can show them. */
  onToolCallArguments?: (toolName: string, args: Record<string, unknown>) => void;
  onToolCallComplete?: (toolCall: ToolCallWithResult) => void;
  onComplete?: (result: StreamV1ChatResult) => void;
  onError?: (error: Error) => void;
  signal?: AbortSignal;
}

/**
 * Extract the system prompt from the messages array (if any).
 */
export function extractSystemPrompt(messages: ApiMessage[]): string | undefined {
  const systemMessages = messages.filter(m => m.role === 'system');
  if (systemMessages.length === 0) return undefined;
  return systemMessages
    .map(m => typeof m.content === 'string' ? m.content : '')
    .filter(Boolean)
    .join('\n\n');
}

/**
 * Convert ContentPart[] to v1 API input items.
 */
export function contentPartsToV1Input(parts: ContentPart[]): V1InputItem[] {
  const items: V1InputItem[] = [];
  for (const part of parts) {
    if (part.type === 'text') {
      items.push({ type: 'text', content: part.text });
    } else if (part.type === 'image_url') {
      items.push({
        type: 'image',
        data_url: part.image_url.url,
        // Our extension — the LB reads it to emit an editing handle and strips
        // it before forwarding, so it never reaches LM Studio (tdd-0076).
        ...(part.image_url.storage_key ? { storage_key: part.image_url.storage_key } : {}),
      });
    } else if (part.type === 'file_url') {
      // Forwarded as a `file` item for the LB to convert. These used to be
      // dropped here on the grounds that the v1 API doesn't support them —
      // true of LM Studio, but the LB sits in front and rewrites them, so
      // discarding client-side meant no document ever reached the model and
      // the "attached: report.pdf" chip in the UI was telling the user
      // something that had not happened.
      items.push({
        type: 'file',
        url: part.file_url.url,
        ...(part.file_url.filename ? { filename: part.file_url.filename } : {}),
      });
    }
  }
  return items;
}

/**
 * Build the `input` field for LM Studio's /api/v1/chat from the latest user message.
 *
 * When using `previous_response_id` for multi-turn, we only send the new user input.
 * When no previous response exists (first message), we send just the user's message.
 *
 * The v1 API expects either a plain string or an array of
 * `{ type: "text", content }` / `{ type: "image", data_url }` objects, plus
 * our `{ type: "file", url }` extension which the LB converts before forwarding.
 */
/**
 * Flatten the transcript into role-tagged entries the LB understands.
 *
 * The LB accepts `{ role, content }` alongside its own item form, and keeps only
 * string content — so multi-part messages are reduced to their text. Images are
 * dropped here deliberately: they ride the current turn via the item form, and
 * re-sending every past image would grow the request without bound.
 *
 * Assistant turns are included; without them the model sees its own replies
 * missing and cannot refer to what it just said or did.
 */
export function buildV1History(messages: ApiMessage[]): Array<{ role: string; content: string }> {
  const out: Array<{ role: string; content: string }> = [];

  for (const m of messages) {
    // The system prompt travels in its own field, not the message list.
    if (m.role === 'system') continue;

    const text =
      typeof m.content === 'string'
        ? m.content
        : Array.isArray(m.content)
          ? m.content
              .map((part) =>
                part && typeof part === 'object' && 'text' in part
                  ? String((part as { text?: unknown }).text ?? '')
                  : '',
              )
              .filter(Boolean)
              .join('\n')
          : '';

    if (!text.trim()) continue;
    out.push({ role: m.role, content: text });
  }

  return out;
}

/**
 * The latest user turn's attachments, as v1 input items — images and files, no
 * text.
 *
 * This is the other half of `buildV1History`: that function flattens the
 * transcript to text, so anything that is not text has to be recovered here or
 * it never reaches the wire. Scoped to the LAST user message on purpose —
 * carrying every historical image would grow the request without bound, which
 * is the cost the flattening exists to avoid.
 *
 * `storage_key` survives on image items: it is what the LB turns into an
 * editing handle, and it is the difference between the model being able to name
 * the image for `edit_image` and having to guess (tdd-0076).
 */
export function latestUserAttachmentItems(messages: ApiMessage[]): V1InputItem[] {
  const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
  if (!lastUserMsg || !Array.isArray(lastUserMsg.content)) return [];

  return contentPartsToV1Input(lastUserMsg.content).filter(
    (item) => 'type' in item && item.type !== 'text',
  );
}

/**
 * Exported so the contract-test fixtures are generated by the SAME code that
 * builds real requests.
 *
 * A fixture written by hand is only ever a restatement of what its author
 * believed the wire format to be — which is exactly how `{type:'message'}`
 * survived in tests while every real request 400'd at the worker. Generating
 * fixtures from this function means a change here breaks the fixtures, and a
 * stale fixture is a visible diff rather than a silent lie.
 */
export function buildV1Input(
  messages: ApiMessage[],
  /**
   * Send the WHOLE conversation rather than just the latest turn.
   *
   * Normally history lives server-side: we send one message and
   * `previous_response_id` lets LM Studio reattach the rest. But the LB's
   * orchestrator (used whenever `integrations` are present) bypasses that store
   * and posts statelessly to /v1/chat/completions — so with tools attached the
   * model received literally one message. "again", "same but bigger", or any
   * reference to a previous turn had nothing to resolve against, which read as
   * the model being forgetful when it simply had no context.
   *
   * Only for the tool path: the passthrough still resolves history server-side,
   * and re-sending it there would duplicate every turn.
   */
  includeHistory = false,
): string | V1InputItem[] {
  if (includeHistory) {
    const history = buildV1History(messages);

    // The latest turn's attachments have to ride ALONGSIDE the history.
    //
    // `buildV1History` reduces every message to its text, which is right for
    // past turns (re-sending old images would grow the request without bound)
    // but silently discarded the CURRENT turn's image — so "edit this picture"
    // reached the model as text with nothing attached, and it replied asking
    // for the image it had just been sent. Text-only turns were unaffected,
    // which is why this survived: the history path is only taken when tools are
    // attached, and the image scenarios were all exercised without them.
    //
    // Only non-text items are carried over. The text is already in `history` as
    // the final user entry, and appending it again would duplicate the prompt.
    const attachmentItems = latestUserAttachmentItems(messages);

    if (attachmentItems.length > 0) {
      return [...history, ...attachmentItems];
    }

    // A single user turn (first message of a conversation) collapses to the
    // plain-string form the API prefers.
    if (history.length === 1 && history[0].role === 'user') {
      return typeof history[0].content === 'string' ? history[0].content : history;
    }
    if (history.length > 0) return history;
  }

  // Get the last user message
  const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
  if (!lastUserMsg) return '';

  const content = lastUserMsg.content;

  // Plain string content → send as string
  if (typeof content === 'string') {
    return content || '';
  }

  // Multi-part content → convert to v1 input items
  if (Array.isArray(content)) {
    const items = contentPartsToV1Input(content);
    // If there's only one text item, simplify to a plain string. The `in` check
    // narrows away the role-tagged variant, which has no `type`.
    const only = items.length === 1 ? items[0] : undefined;
    if (only && 'type' in only && only.type === 'text') {
      return only.content;
    }
    return items;
  }

  return '';
}

/**
 * Stream chat via LM Studio's native /api/v1/chat endpoint.
 * Supports server-side MCP tool execution via the `integrations` field.
 *
 * SSE event types:
 *   chat.start → reasoning.* → tool_call.* → message.* → chat.end
 */
export async function streamV1Chat({
  apiUrl,
  apiKey,
  messages,
  model,
  modelCapabilities,
  previousResponseId,
  mcpServers,
  includeKnowledgeSummary,
  includeAppleSummary,
  settings,
  reasoningLevel,
  onChunk,
  onReasoningChunk,
  onToolCallStart,
  onToolCallArguments,
  onToolCallComplete,
  onComplete,
  onError,
  signal,
  enableMemory,
  sessionId,
  persistable,
  onRecalledMemories,
  onAttachmentsProcessed,
}: StreamV1ChatOptions): Promise<void> {
  try {
    const requestBody: Record<string, unknown> = {
      model,
      // Tools attached ⇒ the LB orchestrates statelessly, so it needs the whole
      // conversation. Without them the passthrough reattaches history itself.
      input: buildV1Input(messages, Boolean(mcpServers && mcpServers.length > 0)),
      stream: true,
      store: true,
    };

    // Extract system prompt from messages, then append the generative-UI
    // capability note.
    //
    // APPENDED, not a persona: rendering a form or a table is a functional
    // building block that should be available whatever persona is active, the
    // same way tool use is. It is only the ~250-token SUMMARY — the actual
    // component syntax (~3k tokens) is pulled on demand via the ui-render MCP
    // server's `list_components`, so a conversation that never renders UI does
    // not pay for the full catalog.
    //
    // Two summaries, deliberately separate: gen-ui's covers components and the
    // form lifecycle, mcp-client's covers what the connected TOOL SERVERS can
    // do. They share this delivery channel and nothing else — folding tool
    // signposts into the gen-ui one made that file read as "everything
    // model-facing", which is not what it is.
    const systemPrompt = extractSystemPrompt(messages);
    const toolSummary = toolCapabilitySummary({ includeKnowledge: includeKnowledgeSummary, includeApple: includeAppleSummary });
    const capabilities = `${CAPABILITY_SUMMARY}\n\n${toolSummary}`;
    const withCapabilities = systemPrompt
      ? `${systemPrompt}\n\n${capabilities}`
      : capabilities;
    requestBody.system_prompt = withCapabilities;

    // Use previous_response_id for multi-turn conversation continuity
    if (previousResponseId) {
      requestBody.previous_response_id = previousResponseId;
    }

    // Reference mcp.json servers for tool use
    if (mcpServers && mcpServers.length > 0) {
      requestBody.integrations = mcpServers;
    }

    // Map generation settings to v1 API params
    if (settings) {
      if (settings.temperature !== undefined) requestBody.temperature = settings.temperature;
      if (settings.maxTokens !== undefined && settings.maxTokens > 0) requestBody.max_output_tokens = settings.maxTokens;
      if (settings.topP !== undefined) requestBody.top_p = settings.topP;
      if (settings.topK !== undefined) requestBody.top_k = settings.topK;
      if (settings.minP !== undefined) requestBody.min_p = settings.minP;
      if (settings.repeatPenalty !== undefined) requestBody.repeat_penalty = settings.repeatPenalty;
      if (settings.presencePenalty !== undefined) requestBody.presence_penalty = settings.presencePenalty;
      if (settings.repetitionPenalty !== undefined) requestBody.repetition_penalty = settings.repetitionPenalty;
    }

    // Reasoning control — only send if model supports it (sending to unsupported models causes 400)
    if (reasoningLevel && modelCapabilities?.includes('reasoning')) {
      requestBody.reasoning = reasoningLevel;
    }

    const { data: stream } = await llmApiClient.post<ReadableStream>(
      `${apiUrl}/api/v1/chat`,
      requestBody,
      {
        responseType: 'stream',
        headers: {
          ...(apiKey && { Authorization: `Bearer ${apiKey}` }),
          // Opt into LB-side Brain recall (LB still gates on auth + origin allowlist).
          ...(enableMemory ? { 'x-brain-recall': 'on' } : {}),
          // Where to put this turn's result if the connection dies before it
          // arrives (tdd-0090). Both are required for write-back: the id says
          // WHERE, and the flag says WHETHER. A private thread sends
          // `x-chat-persistable: off` and is never written server-side.
          ...(sessionId ? { 'x-chat-session-id': sessionId } : {}),
          ...(persistable === undefined
            ? {}
            : { 'x-chat-persistable': persistable ? 'on' : 'off' }),
        },
        signal,
      }
    );

    const reader = stream?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let accumulatedContent = '';
    let accumulatedReasoning = '';
    let responseId: string | undefined;
    const completedToolCalls: ToolCallWithResult[] = [];

    // Track in-progress tool call for streaming events
    let currentToolName = '';
    let currentToolArgs: Record<string, unknown> = {};
    let toolCallCounter = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        // LM Studio v1 uses "event:" + "data:" format
        // But also handle plain "data:" lines
        if (trimmed.startsWith('data: ')) {
          try {
            const json = JSON.parse(trimmed.slice(6));
            const eventType = json.type as string;

            if (!eventType) continue;

            switch (eventType) {
              // Message content streaming
              case 'message.delta': {
                if (json.content) {
                  accumulatedContent += json.content;
                  onChunk(json.content);
                }
                break;
              }

              // Reasoning/thinking streaming
              case 'reasoning.delta': {
                if (json.content) {
                  accumulatedReasoning += json.content;
                  onReasoningChunk?.(json.content);
                }
                break;
              }

              // Centralized Brain recall (tdd-0042): the LB injected memories and
              // tells us what, so the client can show its recall UI.
              case 'memory.recalled': {
                if (Array.isArray(json.facts) && json.facts.length > 0) {
                  onRecalledMemories?.(json.facts as string[], json.query as string | undefined);
                }
                break;
              }

              // What the LB did with each attachment (tdd-0062). Only the server
              // knows: the inline-vs-index choice depends on extracted text size.
              case 'attachments.processed': {
                if (Array.isArray(json.attachments) && json.attachments.length > 0) {
                  onAttachmentsProcessed?.(json.attachments as AttachmentOutcome[]);
                }
                break;
              }

              // Tool call lifecycle. The LB emits these in order per tool:
              //   tool_call.start      (no name yet)
              //   tool_call.name       { tool_name }
              //   tool_call.arguments  { tool, arguments }
              //   tool_call.success    { tool, output }
              // The name arrives in its OWN event, so onToolCallStart can't carry
              // it — we notify the UI once we have the name (tool_call.name) so the
              // pending card is named and reconciles with .success.
              case 'tool_call.start': {
                currentToolName = '';
                currentToolArgs = {};
                break;
              }

              case 'tool_call.name': {
                currentToolName = json.tool_name || json.tool || currentToolName;
                onToolCallStart?.(currentToolName);
                break;
              }

              case 'tool_call.arguments': {
                currentToolName = json.tool || currentToolName;
                if (json.arguments) {
                  currentToolArgs = json.arguments;
                  // Push them to the UI NOW rather than only at .success.
                  //
                  // The pending card is created by onToolCallStart (from
                  // .name, which arrives first and carries no arguments), so
                  // without this it renders `{}` for the whole call — up to
                  // five minutes for an image edit — and the arguments appear
                  // only once the tool has already finished. The wire had them
                  // all along; the UI was showing its own placeholder.
                  onToolCallArguments?.(currentToolName, currentToolArgs);
                }
                break;
              }

              case 'tool_call.success': {
                const toolCall: ToolCallWithResult = {
                  id: `tc-${++toolCallCounter}`,
                  name: json.tool || currentToolName,
                  arguments: json.arguments || currentToolArgs,
                  status: 'completed',
                  result: {
                    content: [{ type: 'text', text: json.output || '' }],
                  },
                };
                completedToolCalls.push(toolCall);
                onToolCallComplete?.(toolCall);
                currentToolName = '';
                currentToolArgs = {};
                break;
              }

              case 'tool_call.failure': {
                const failedCall: ToolCallWithResult = {
                  id: `tc-${++toolCallCounter}`,
                  name: json.tool || currentToolName,
                  arguments: json.arguments || currentToolArgs,
                  status: 'error',
                  error: json.reason || json.output || 'Tool call failed',
                };
                completedToolCalls.push(failedCall);
                onToolCallComplete?.(failedCall);
                currentToolName = '';
                currentToolArgs = {};
                break;
              }

              // Final aggregated result
              case 'chat.end': {
                // Capture response_id for multi-turn continuity
                if (json.response_id) {
                  responseId = json.response_id;
                }

                // The chat.end event contains the full result with output array
                // We've already accumulated everything via streaming events,
                // but we can use this to fill in any gaps
                if (json.result?.output) {
                  // If we somehow missed tool calls, extract from output
                  const outputItems = json.result.output as V1OutputItem[];
                  for (const item of outputItems) {
                    if (item.type === 'tool_call') {
                      const alreadyTracked = completedToolCalls.some(
                        tc => tc.name === item.tool && JSON.stringify(tc.arguments) === JSON.stringify(item.arguments)
                      );
                      if (!alreadyTracked) {
                        completedToolCalls.push({
                          id: `tc-${++toolCallCounter}`,
                          name: item.tool || 'unknown',
                          arguments: item.arguments || {},
                          status: 'completed',
                          result: {
                            content: [{ type: 'text', text: item.output || '' }],
                          },
                        });
                      }
                    }
                  }
                }
                break;
              }

              // Ignore lifecycle events we don't need
              case 'chat.start':
              case 'message.start':
              case 'message.end':
              case 'reasoning.start':
              case 'reasoning.end':
              case 'model_load.start':
              case 'model_load.progress':
              case 'model_load.end':
              case 'prompt_processing.start':
              case 'prompt_processing.progress':
              case 'prompt_processing.end':
                break;

              case 'error': {
                let errorMsg = 'Unknown streaming error';
                if (typeof json.message === 'string') {
                  errorMsg = json.message;
                } else if (typeof json.error === 'string') {
                  errorMsg = json.error;
                } else if (json.error && typeof json.error === 'object') {
                  // LLM LB returns {error: {message, type, code}}
                  errorMsg = json.error.message || JSON.stringify(json.error);
                }
                onError?.(new Error(errorMsg));
                return;
              }

              default:
                // Unknown event type — log but don't fail
                console.debug(`[v1chat] Unknown event type: ${eventType}`);
            }
          } catch {
            // Ignore JSON parse errors for malformed chunks
          }
        }
      }
    }

    onComplete?.({
      content: accumulatedContent,
      toolCalls: completedToolCalls,
      reasoning: accumulatedReasoning,
      responseId,
    });
  } catch (error) {
    // Request was cancelled by user — don't treat as error
    if (error instanceof Error && error.name === 'AbortError') {
      return;
    }
    if (error instanceof NetworkError && error.code === 'ABORT') {
      return;
    }

    // Extract meaningful error message
    let err: Error;
    if (error instanceof NetworkError) {
      // Include response data if available (API error details)
      const detail = error.responseData
        ? (typeof error.responseData === 'string' ? error.responseData : JSON.stringify(error.responseData))
        : '';
      err = new Error(detail ? `${error.message}: ${detail}` : error.message);
    } else if (error instanceof Error) {
      err = error;
    } else {
      err = new Error(typeof error === 'object' ? JSON.stringify(error) : String(error));
    }
    onError?.(err);
  }
}
